import React, { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce'
import Svg from '../../../../../common/svgIcon';
import { multiplesInfo, accaNames } from '../../../../../common/utils/multiples'
import EachWay from '../../../../../common/eachWay';
import StakeField from '../../../../../common/stakeField';
import NumericKeypad from '../../../../../common/numericKeypad';
import ErrorBanner from '../../../../../common/errorBanner';
import { getMultipleEstimatedReturnText } from '../../../../../common/utils/estimatedReturn';
import getSelectedBetsForMultiples from '../../../../../common/utils/getSelectedBetsForMultiples';
import getMultipleOddsFraction from '../../../../../common/utils/multiplesOdds';
import PRICE_TYPES from '../../../../../common/utils/priceTypes'
import { ERROR_CODES, ERROR_MESSAGES } from '../../../../../common/utils/apiErrors'
import cleanStakeInput from '../../../../../common/utils/cleanStakeInput'
import './styles.scss';

const BANNER_DISPLAY_DELAY = 800

const getNewStake = (rawStakeValue) => {
    const cleaned = parseFloat(cleanStakeInput(rawStakeValue))
    return (isNaN(cleaned)) ? 0 : cleaned
}

export class MultipleForm extends Component {

    onStakeUpdated = (rawStakeValue) => {
        this.dismissStakeErrors(rawStakeValue)
        this.displayStakeErrors(rawStakeValue)
    }

    onToggleEachWay = (e) => {
        const {
            optionId,
            setIsEachWayForMultiple,
        } = this.props;

        setIsEachWayForMultiple(optionId, e.target.checked);
    }

    getEstimatedReturn = () => {
        const {
            betType,
            betSelections,
            selectedBets,
            stakeValue,
            currencySign,
            isEachWay,
        } = this.props;

        if (stakeValue) {
            const partialMultipleData = {
                stakeValue,
                betType,
                isEachWay,
                betSelections,
            }
            const includedSelectedBets = selectedBets.filter(
                bet => bet.includeInMultiples,
            )
            const estimatedReturn = getMultipleEstimatedReturnText(
                includedSelectedBets,
                partialMultipleData,
                currencySign,
            )

            return (
                <div className="bs-multiple-form__inline-returns">
                    <span>Est. Returns:</span>
                    <span className="bs-betForm__estReturns">
                        {estimatedReturn}
                    </span>
                </div>
            )
        }

        return null;
    }

    displayStakeErrors = debounce((rawStakeValue) => {
        const {
            optionId,
            currencySign,
            setMultiplesError,
            minStake,
            maxStake,
        } = this.props

        // Check if a min or max stake error should now be dismissed or restored
        if (rawStakeValue) {
            const newStake = getNewStake(rawStakeValue)

            if (minStake !== null && newStake < minStake) {
                // Restore a stake-too-low error
                const message = ERROR_MESSAGES.STAKE_TOO_LOW
                    .replace('%PRICE%', `${currencySign}${minStake.toFixed(2)}`)
                setMultiplesError(
                    optionId,
                    ERROR_CODES.STAKE_TOO_LOW,
                    message,
                )
            }

            if (maxStake !== null && newStake > maxStake) {
                // Restore a stake-too-high error
                const message = ERROR_MESSAGES.STAKE_TOO_HIGH
                    .replace('%PRICE%', `${currencySign}${maxStake.toFixed(2)}`)
                setMultiplesError(
                    optionId,
                    ERROR_CODES.STAKE_TOO_HIGH,
                    message,
                )
            }
        }
    }, BANNER_DISPLAY_DELAY)

    dismissStakeErrors = (rawStakeValue) => {
        const {
            optionId,
            error,
            clearMultiplesError,
            minStake,
            maxStake,
        } = this.props

        // Check if a min or max stake error should now be dismissed or restored
        if (rawStakeValue) {
            const newStake = getNewStake(rawStakeValue)

            const shouldClearMinError =
                minStake !== null &&
                error.errorId === ERROR_CODES.STAKE_TOO_LOW &&
                newStake >= minStake

            const shouldClearMaxError =
                maxStake !== null &&
                error.errorId === ERROR_CODES.STAKE_TOO_HIGH &&
                newStake <= maxStake

            if (shouldClearMinError || shouldClearMaxError) {
                clearMultiplesError(optionId)
            }
        }
    }

    renderNumericKeyPad = () => {
        const {
            optionId,
            isMobile,
            stakeValue,
            isEditingStake,
            setIsEditingStake,
            setStake,
        } = this.props

        return (isMobile && isEditingStake) ? (
            <NumericKeypad
                targetId={optionId}
                setIsEditingStake={setIsEditingStake}
                setStake={setStake}
                value={stakeValue}
                maxLength={8}
            />

        ) : null;
    }

    renderMultipleOdds = () => {
        const {
            betSelections,
            numberLines,
            selectedBets,
        } = this.props;

        let filterBets;
        let multipleOdds;

        if (numberLines === 1) {
            filterBets = getSelectedBetsForMultiples(
                betSelections,
                selectedBets,
            ).filter(bet => bet !== undefined);
            const spBet = filterBets.find(
                bet => bet.priceType === PRICE_TYPES.SP || bet.isSpOnly === true,
            )
            if (spBet) {
                return null
            }
            multipleOdds = getMultipleOddsFraction(filterBets)
            const formatted = multipleOdds.toFixed(2).replace('.00', '')

            return (
                <div className="bs-multiple-form__odds">
                    {`${formatted}/1`}
                </div>
            )
        }

        return null;
    }

    renderEachway = () => {
        const {
            optionId,
            isEachWay,
            setGlobalModal,
            betSelections,
            selectedBets,
        } = this.props;

        let i = betSelections.length;
        let isEachwayAvailable = true;
        let selectedBet;

        const filterBets = getSelectedBetsForMultiples(
            betSelections,
            selectedBets,
        ).filter(bet => bet !== undefined);

        if (filterBets.length !== i) {
            i = filterBets.length;
        }

        while (i && isEachwayAvailable) {
            selectedBet = filterBets[i - 1];
            if (
                selectedBet.eachWayFraction === '' ||
                selectedBet.eachWayFraction === '1/1'
            ) {
                isEachwayAvailable = false;
            }
            i -= 1;
        }

        return (
            <EachWay
                id={optionId}
                isEachWayEnabled={isEachwayAvailable}
                setGlobalModal={setGlobalModal}
                isEachWayChecked={isEachWay}
                onChange={this.onToggleEachWay}
            />
        )
    }

    render() {
        const {
            betType,
            numberLines,
            setGlobalModal,
            optionId,
            stakeValue,
            isEditingStake,
            setStake,
            setIsEditingStake,
            isMobile,
            currencySign,
            error,
            isEachWay,
        } = this.props;

        const multipleMeta = (multiplesInfo[betType])
            ? multiplesInfo[betType]
            : multiplesInfo.accumulator

        const betTitle = (betType.includes('accumulator'))
            ? accaNames[betType]
            : multipleMeta.formTitle

        const errorBanner = (error.message !== null) ?
            <ErrorBanner message={error.message} />
            : null;

        const numberLinesRender = (isEachWay) ? numberLines * 2 : numberLines;

        return (
            <div className="bs-multiple-form">
                {errorBanner}
                <div className="bs-multiple-form__body">
                    <div className="bs-multiple-form__title-row">
                        <div className="bs-multiple-form__title">
                            <button
                                type="button"
                                className="bs-multiple-form__info"
                                onClick={() => {
                                    setGlobalModal({
                                        title: multipleMeta.title,
                                        body: multipleMeta.body,
                                        ctaTitle: 'OK',
                                    })
                                }}
                            >
                                <Svg
                                    iconName="multiple-info"
                                    size="Medium"
                                    className="bs-multiple-form__info-icon"
                                />
                            </button>
                            {`${betTitle} (x${numberLinesRender})`}
                        </div>
                        { this.renderMultipleOdds() }
                    </div>

                    <div className="bs-multiple-form__stake-row">
                        <div className="bs-multiple-form__stake">
                            { this.renderEachway() }
                            <StakeField
                                targetId={optionId}
                                currencySign={currencySign}
                                stakeValue={stakeValue}
                                setStake={setStake}
                                setIsEditingStake={setIsEditingStake}
                                isEditingStake={isEditingStake}
                                isMobile={isMobile}
                                onStakeUpdated={this.onStakeUpdated}
                                placeholder={
                                    (isEachWay || numberLines > 1)
                                    ? 'Unit Stake'
                                    : 'Stake'
                                }
                            />
                        </div>
                    </div>
                    <div className="bs-multiple-form__inline-returns-row">
                        { this.getEstimatedReturn() }
                    </div>
                    { this.renderNumericKeyPad() }
                </div>
            </div>
        );
    }
}

MultipleForm.defaultProps = {
    numberLines: 0,
    stakeValue: '',
    error: {
        message: null,
        errorId: null,
    },
    isEditingStake: false,
    isEachWay: false,
    minStake: null,
    maxStake: null,
}

MultipleForm.propTypes = {
    betType: PropTypes.string.isRequired,
    optionId: PropTypes.string.isRequired,
    stakeValue: PropTypes.string,
    currencySign: PropTypes.string.isRequired,

    numberLines: PropTypes.number,

    setStake: PropTypes.func.isRequired,
    setIsEditingStake: PropTypes.func.isRequired,
    setGlobalModal: PropTypes.func.isRequired,
    setIsEachWayForMultiple: PropTypes.func.isRequired,

    isMobile: PropTypes.bool.isRequired,
    isEditingStake: PropTypes.bool,
    isEachWay: PropTypes.bool,

    error: PropTypes.shape({
        message: PropTypes.string,
        errorId: PropTypes.string,
    }),
    setMultiplesError: PropTypes.func.isRequired,
    clearMultiplesError: PropTypes.func.isRequired,
    minStake: PropTypes.number,
    maxStake: PropTypes.number,

    selectedBets: PropTypes.arrayOf(
        PropTypes.shape({
            outcomersId: PropTypes.string.isRequired,
            raceTime: PropTypes.string.isRequired,
            courseName: PropTypes.string.isRequired,
            horseName: PropTypes.string.isRequired,
            fractionalPrice: PropTypes.string.isRequired,
            decimalPrice: PropTypes.number, // SP-only bets will not always have this
            isSpOnly: PropTypes.bool.isRequired,
            eachWayFraction: PropTypes.string,
            eachWayPlaces: PropTypes.string,
            priceType: PropTypes.string,
            isBog: PropTypes.string,
        }),
    ).isRequired,

    betSelections: PropTypes.arrayOf(
        PropTypes.shape({
            selectionId: PropTypes.string.isRequired,
            eventId: PropTypes.string.isRequired,
            marketId: PropTypes.string.isRequired,
        }),
    ).isRequired,

}

export default MultipleForm



// WEBPACK FOOTER //
// /src/js/drawer/components/BetPage/components/MultipleForm/MultipleForm.jsx